import { useMemo } from 'react';
import { FormApi } from 'final-form';

import { useIsUserPermittedTo } from '@occc/utils-access-control';
import { validateScheduleDatesForm } from '@occc-app/routes/CampaignComponents/components/ScheduleDates/schemas';
import { ScheduleFormValues, ScheduleFormProps, ScheduleFormPropsHookArgs } from './types';

import { getScheduleOperations } from './getScheduleOperations';
import { transformOfferToScheduleValues } from './transformOfferToScheduleInitialValues';
import { getScheduleVisibility } from './utils';
import { GetFormPropsHook } from '../types';
import { mapFormTypeToPermission } from '../utils/offerPermission';
import { useExecuteOperations } from '../operations/useExecuteOperations';

export const useGetScheduleFormProps: GetFormPropsHook<
  ScheduleFormPropsHookArgs,
  ScheduleFormProps
> = ({ offerId, offer, onFormSubmitted, onError, onCancel, currentFlowType, type }) => {
  const [executeOperations, loading] = useExecuteOperations<ScheduleFormValues>({
    offerId,
    offer,
    getOperations: getScheduleOperations,
  });

  const isFormEditable = useIsUserPermittedTo(mapFormTypeToPermission[type]);
  const triggerVisibility = useMemo(() => getScheduleVisibility(offer, type), [offer, type]);
  const initialValues: Partial<ScheduleFormValues> = useMemo(() => {
    if (offer) {
      return transformOfferToScheduleValues(offer);
    }

    return {
      triggerPreferences: {
        triggerOnFulfillment: true,
        triggerOnVisibility: false,
        triggerOnProgress: false,
      },
    };
  }, [offer]);

  const onSubmit = async (values: ScheduleFormValues, form: FormApi<ScheduleFormValues>) => {
    try {
      await executeOperations(values, form.getState());
      await onFormSubmitted();
    } catch (error) {
      onError(error);
    }
  };

  return {
    initialValues,
    loading,
    currentFlowType,
    validate: validateScheduleDatesForm,
    onSubmit,
    onCancelClick: onCancel,
    isFormEditable,
    isTriggerOnVisibilityShown: !!triggerVisibility.TriggerOnVisibility,
    isTriggerOnFulfillmentShown: !!triggerVisibility.TriggerOnFulfillment,
    isTriggerOnProgressShown: !!triggerVisibility.TriggerOnProgress,
  };
};
